import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';

type QuizOption = { id: number; text: string };
type QuizQuestion = { id: number; text: string; kind: 'single' | 'multi'; options: QuizOption[] };
type QuizResponse = {
  id: number;
  homework_id: number;
  time_limit_seconds: number | null;
  questions: QuizQuestion[];
};

type StartResponse = {
  submission_id: number;
  started_at: string;
  time_limit_seconds: number | null;
  already_submitted: boolean;
  submitted_at?: string;
  score?: number | null;
  total?: number | null;
  duration_seconds?: number | null;
};

type Answer = { question_id: number; selected_option_ids: number[] };

interface Props {
  open: boolean;
  homeworkId: number;
  onClose: () => void;
  readOnly?: boolean;
  resultSummary?: {
    score?: number | null;
    total?: number | null;
    durationSeconds?: number | null;
    submittedAt?: string | null;
  } | null;
}

const HomeworkQuizModal: React.FC<Props> = ({ open, homeworkId, onClose, readOnly = false, resultSummary = null }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quiz, setQuiz] = useState<QuizResponse | null>(null);
  const [start, setStart] = useState<StartResponse | null>(null);
  const [answers, setAnswers] = useState<Record<number, number[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ score: number; total: number } | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    setAnswers({});
    setQuiz(null);
    setStart(null);
    setLoading(true);

    const requests: Array<Promise<any>> = [api.get(`/api/teme/${encodeURIComponent(String(homeworkId))}/quiz`)];
    if (!readOnly) {
      requests.push(api.post(`/api/teme/${encodeURIComponent(String(homeworkId))}/quiz/start`, {}));
    }

    Promise.all(requests)
      .then((resp) => {
        const q = resp[0];
        const s = resp[1];
        setQuiz(q.data as any);

        if (readOnly) {
          const score = Number((resultSummary as any)?.score ?? NaN);
          const total = Number((resultSummary as any)?.total ?? NaN);
          if (Number.isFinite(score) && Number.isFinite(total)) setResult({ score, total });
          const dur = (resultSummary as any)?.durationSeconds;
          if (dur != null && Number.isFinite(Number(dur))) setDurationSeconds(Number(dur));
          return;
        }

        setStart(s.data as any);
        if ((s.data as any)?.already_submitted) {
          const score = Number((s.data as any)?.score ?? 0);
          const total = Number((s.data as any)?.total ?? 0);
          if (Number.isFinite(score) && Number.isFinite(total)) setResult({ score, total });
          const dur = (s.data as any)?.duration_seconds;
          if (dur != null && Number.isFinite(Number(dur))) setDurationSeconds(Number(dur));
        }
      })
      .catch((e: any) => {
        setError(e?.response?.data?.detail || 'Nu am putut încărca quizul.');
      })
      .finally(() => setLoading(false));
  }, [homeworkId, open, readOnly, resultSummary]);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNowMs(Date.now()), 500);
    return () => clearInterval(t);
  }, [open]);

  const timeLeft = useMemo(() => {
    if (readOnly) return null;
    if (!start?.time_limit_seconds) return null;
    const startedAt = start.started_at ? new Date(start.started_at).getTime() : null;
    if (!startedAt) return null;
    const deadline = startedAt + start.time_limit_seconds * 1000;
    return Math.max(0, deadline - nowMs);
  }, [nowMs, readOnly, start?.started_at, start?.time_limit_seconds]);

  const durationLabel = useMemo(() => {
    if (durationSeconds == null) return null;
    const sec = Math.max(0, Math.trunc(durationSeconds));
    const mm = Math.floor(sec / 60);
    const ss = sec % 60;
    return `${mm}:${String(ss).padStart(2, '0')}`;
  }, [durationSeconds]);

  const timeLeftLabel = useMemo(() => {
    if (timeLeft == null) return null;
    const s = Math.ceil(timeLeft / 1000);
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${String(ss).padStart(2, '0')}`;
  }, [timeLeft]);

  if (!open) return null;

  const toggleOption = (q: QuizQuestion, optionId: number) => {
    setAnswers((prev) => {
      const cur = prev[q.id] ?? [];
      if (q.kind === 'single') return { ...prev, [q.id]: [optionId] };
      const has = cur.includes(optionId);
      return { ...prev, [q.id]: has ? cur.filter((x) => x !== optionId) : [...cur, optionId] };
    });
  };

  const submit = async () => {
    if (!quiz || !start) return;
    if (start.already_submitted) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        submission_id: start.submission_id,
        answers: quiz.questions.map((q) => ({ question_id: q.id, selected_option_ids: answers[q.id] ?? [] })) as Answer[],
      };
      const resp = await api.post(`/api/teme/${encodeURIComponent(String(homeworkId))}/quiz/submit`, payload);
      const score = Number((resp.data as any)?.score ?? 0);
      const total = Number((resp.data as any)?.total ?? quiz.questions.length);
      const dur = (resp.data as any)?.duration_seconds;
      if (dur != null && Number.isFinite(Number(dur))) setDurationSeconds(Number(dur));
      setResult({ score, total });
      setStart((p) => (p ? { ...p, already_submitted: true, score, total } : p));
    } catch (e: any) {
      const msg = (e?.response?.data?.detail || '').toString();
      if (msg.toLowerCase().includes('expirat')) {
        setError('Nu se mai poate trimite quizul.');
      } else {
        setError(msg || 'Nu am putut trimite quizul.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
      <div
        className="w-full max-w-2xl bg-card border border-border rounded-xl p-5 max-h-[85vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-foreground">Quiz</div>
            {timeLeftLabel ? <div className="text-xs text-muted-foreground mt-1">Timp rămas: {timeLeftLabel}</div> : null}
          </div>
          <button
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
            onClick={onClose}
            aria-label="Închide"
            type="button"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">Se încarcă…</div>
        ) : error ? (
          <div className="text-sm text-error">{error}</div>
        ) : result ? (
            <div className="border border-border rounded-lg p-4 bg-muted/20">
              <div className="text-sm font-medium text-foreground">Rezultat</div>
              <div className="text-sm text-muted-foreground mt-1">
                {result.score} / {result.total}
              </div>
              {durationLabel ? <div className="text-sm text-muted-foreground mt-1">Timp rezolvare: {durationLabel}</div> : null}
            </div>
        ) : quiz ? (
          <div className="space-y-4">
            {readOnly ? (
              <div className="border border-border rounded-lg p-3 bg-muted/20 text-sm text-muted-foreground">
                Doar elevul poate completa quizul. Aici este disponibil doar pentru vizualizare.
              </div>
            ) : null}
            {quiz.questions.map((q, idx) => (
              <div key={q.id} className="border border-border rounded-lg p-4">
                <div className="text-sm font-semibold text-foreground">
                  {idx + 1}. {q.text}
                </div>
                <div className="mt-2 space-y-2">
                  {q.options.map((o) => {
                    const checked = (answers[q.id] ?? []).includes(o.id);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => (readOnly ? null : toggleOption(q, o.id))}
                        disabled={readOnly}
                        className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                          checked ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            readOnly
                            type={q.kind === 'single' ? 'radio' : 'checkbox'}
                            checked={checked}
                            className="h-4 w-4"
                          />
                          <div className="text-sm text-foreground">{o.text}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Închide
              </Button>
              {!readOnly && (
                <Button variant="default" onClick={() => void submit()} disabled={submitting}>
                {submitting ? 'Se trimite…' : 'Trimite quizul'}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Quiz inexistent.</div>
        )}
      </div>
    </div>
  );
};

export default HomeworkQuizModal;
